import { addProtocol } from 'maplibre-gl';
import { PMTiles, Protocol as PMTilesProtocol } from 'pmtiles';
import { useEffect, useState } from 'react';
export const gamePMTilesSourceId = 'game-pmtiles';
export function useGamePMTiles({
  game,
  map,
  mapInitialized,
  computeFeatureState
}) {
  const [sourceLayer, setSourceLayer] = useState(null);
  useEffect(() => {
    const pmTilesFileUrl = game?.pmTilesFileUrl;
    if (!pmTilesFileUrl) return;
    let cancelled = false;
    async function load() {
      const protocol = new PMTilesProtocol();
      addProtocol('pmtiles', protocol.tile);
      const tiles = new PMTiles(pmTilesFileUrl);
      protocol.add(tiles);
      const [header, metadata] = await Promise.all([tiles.getHeader(), tiles.getMetadata()]);
      if (cancelled) return;
      console.info('[useGamePMTiles] header', header);
      console.info('[useGamePMTiles] metadata', metadata);
      const vectorLayers = metadata?.vector_layers;
      setSourceLayer(vectorLayers?.[0]?.id || 'polygons');
    }
    load().catch(err => console.error('[useGamePMTiles]', err));
    return () => {
      cancelled = true;
    };
  }, [game]);
  useEffect(() => {
    const pmTilesFileUrl = game?.pmTilesFileUrl;
    if (!pmTilesFileUrl || !map || !mapInitialized) return;
    if (!map.getSource(gamePMTilesSourceId)) {
      map.addSource(gamePMTilesSourceId, {
        type: 'vector',
        url: `pmtiles://${pmTilesFileUrl}`,
        promoteId: 'h3_index'
      });
    }
  }, [game, map, mapInitialized]);
  useEffect(() => {
    if (!map || !mapInitialized || !sourceLayer || !computeFeatureState) return;
    const layer = sourceLayer;
    let previousIds = new Set();
    let stateKeys;
    let refreshTimeout;
    function applyFeatureStates() {
      const features = map.querySourceFeatures(gamePMTilesSourceId, {
        sourceLayer: layer
      });
      const currentIds = new Set();
      for (const feature of features) {
        const id = feature.id;
        if (id === undefined || currentIds.has(id)) continue;
        currentIds.add(id);
        const state = computeFeatureState(feature.properties);
        if (state) {
          stateKeys ??= Object.keys(state);
          map.setFeatureState({
            source: gamePMTilesSourceId,
            sourceLayer: layer,
            id
          }, state);
        }
      }
      if (stateKeys) {
        for (const id of previousIds) {
          if (currentIds.has(id)) continue;
          for (const key of stateKeys) {
            map.removeFeatureState({
              source: gamePMTilesSourceId,
              sourceLayer: layer,
              id
            }, key);
          }
        }
      }
      previousIds = currentIds;
    }
    function scheduleApplyFeatureStates() {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(applyFeatureStates, 150);
    }
    function handleSourceData(event) {
      if (event.sourceId === gamePMTilesSourceId && event.isSourceLoaded) scheduleApplyFeatureStates();
    }
    map.on('sourcedata', handleSourceData);
    map.on('moveend', scheduleApplyFeatureStates);
    applyFeatureStates();
    return () => {
      clearTimeout(refreshTimeout);
      map.off('sourcedata', handleSourceData);
      map.off('moveend', scheduleApplyFeatureStates);
    };
  }, [map, mapInitialized, sourceLayer, computeFeatureState]);
  return {
    sourceLayer
  };
}